import { getConvertApiToken } from '../auth'
import { convertApiUrl } from '../config'

/**
 * Client for the MarkItDown conversion API.
 *
 * The API produces markedly better text than the browser-side parsers — it keeps
 * tables as tables, headings as headings, and handles formats (xlsx, pptx, msg)
 * that pdf.js and mammoth cannot touch. But it is optional infrastructure, so
 * every failure here is recoverable: the caller falls back to local parsing.
 */

export class ConvertApiUnavailable extends Error {}

/** Formats worth a round trip. Plain text is already readable, so it stays local. */
const REMOTE_EXTENSIONS = new Set([
  '.pdf',
  '.docx',
  '.doc',
  '.pptx',
  '.xlsx',
  '.xls',
  '.msg',
  '.epub',
])

export function isConvertApiConfigured(): boolean {
  return convertApiUrl.length > 0
}

export function shouldConvertRemotely(extension: string): boolean {
  return isConvertApiConfigured() && REMOTE_EXTENSIONS.has(extension)
}

interface ConversionResponse {
  name: string
  markdown: string
  characters: number
}

/**
 * Converts a file to Markdown via the API.
 *
 * Throws {@link ConvertApiUnavailable} when the service could not be reached or
 * refused the call for a reason a retry with the local parser might survive.
 * Throws a plain Error when the document itself is the problem, which the local
 * parser would also hit.
 */
export async function convertRemotely(file: File, signal?: AbortSignal): Promise<string> {
  if (!isConvertApiConfigured()) {
    throw new ConvertApiUnavailable('Conversion API is not configured.')
  }

  let token: string
  try {
    token = await getConvertApiToken()
  } catch {
    throw new ConvertApiUnavailable('Could not get a token for the conversion API.')
  }

  const body = new FormData()
  body.append('file', file, file.name)

  let response: Response
  try {
    response = await fetch(`${convertApiUrl}/convert`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body,
      signal,
    })
  } catch (error) {
    // Network error, CORS rejection, or a cold start that outran the browser.
    throw new ConvertApiUnavailable(
      error instanceof Error ? error.message : 'Could not reach the conversion API.',
    )
  }

  if (!response.ok) {
    const detail = await readDetail(response)

    // 4xx that describe the document are final; anything else may be transient
    // infrastructure and deserves a local retry.
    if (response.status === 415 || response.status === 422 || response.status === 413) {
      throw new Error(detail)
    }
    throw new ConvertApiUnavailable(detail)
  }

  const result = (await response.json()) as ConversionResponse
  return result.markdown
}

async function readDetail(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { detail?: unknown }
    if (typeof body.detail === 'string') return body.detail
  } catch {
    // Fall through to the status line.
  }
  return `The conversion API returned ${response.status}.`
}
